'use client';

import debounce from 'lodash.debounce';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';

export interface GlobalSearchState {
  searchOpen: boolean;
  inputValue: string;
  setSearchOpen: (searchOpen: boolean) => void;
  setInputValue: (inputValue: string) => void;
  setInputValueImmediate: (inputValue: string) => void;
  searchValue: string;
}

let storeApi: StoreApi<GlobalSearchState> | undefined;

export const getGlobalSearchStore = () => {
  if (!storeApi) {
    storeApi = createStore<GlobalSearchState>((set, get) => {
      const debounceSearchValue = debounce((value: string) => {
        set({ searchValue: value });
      }, 500);
      return {
        searchOpen: false,
        inputValue: '',
        searchValue: '',
        setSearchOpen: (searchOpen) => set({ searchOpen }),
        setInputValue: (inputValue) => {
          set({ inputValue });
          if (inputValue === '') {
            set({ searchValue: '' });
            return;
          }

          debounceSearchValue(inputValue);
        },
        setInputValueImmediate: (inputValue) => {
          set({ inputValue, searchValue: inputValue });
        },
      };
    });
  }

  return storeApi;
};

export const useGlobalSearchStore = <T>(
  selector: (state: GlobalSearchState) => T
) => {
  return useStore(getGlobalSearchStore(), selector);
};

export const useSearchOpen = () => {
  return useGlobalSearchStore((state) => state.searchOpen);
};

export const useSetSearchOpen = () => {
  return useGlobalSearchStore((state) => state.setSearchOpen);
};

export const useSearchValue = () => {
  const inputSearchValue = useGlobalSearchStore((state) => state.searchValue);
  return useMemo(() => inputSearchValue.trim(), [inputSearchValue]);
};

export const useSetInputState = () => {
  return useGlobalSearchStore(
    (state) => [state.inputValue, state.setInputValue] as const
  );
};

export const useIsShowingPopup = () => {
  return useGlobalSearchStore(
    (state) => state.searchOpen && state.inputValue.trim() !== ''
  );
};

export const useClearSearchWhenPageChange = () => {
  const pathname = usePathname();
  const setSearchOpen = useSetSearchOpen();
  const setInputValueImmediate = useGlobalSearchStore(
    (state) => state.setInputValueImmediate
  );
  useEffect(() => {
    setSearchOpen(false);
    setInputValueImmediate('');
  }, [pathname]);
};
